import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import axios from 'axios';
import { getAuthHeaders } from '../utils/auth';
import { toast, Toaster } from 'sonner';
import {
  TrendingUp, TrendingDown, AlertCircle, RefreshCw, Download,
  CreditCard, Shield, FileText, Target, Activity, Plus, CheckCircle, X
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CreditCommandCenter = () => {
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [providerStatus, setProviderStatus] = useState(null);
  const [scores, setScores] = useState(null);
  const [borrowingPower, setBorrowingPower] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [tradelines, setTradelines] = useState([]);
  const [recommendations, setRecommendations] = useState([]);
  const [disputeCases, setDisputeCases] = useState([]);
  const [activeTab, setActiveTab] = useState('overview');
  
  // Simulation state
  const [showSimulator, setShowSimulator] = useState(false);
  const [simulationResult, setSimulationResult] = useState(null);
  
  // Add account state
  const [showAddAccount, setShowAddAccount] = useState(false);
  const [showConnectProvider, setShowConnectProvider] = useState(false);
  const [connecting, setConnecting] = useState(false);

  useEffect(() => {
    fetchAllData();
  }, []);

  const fetchAllData = async () => {
    try {
      setLoading(true);
      const [
        statusRes, 
        scoresRes, 
        alertsRes,
        tradelinesRes,
        disputesRes
      ] = await Promise.all([
        axios.get(`${API}/credit/provider/status`, { headers: getAuthHeaders() }),
        axios.get(`${API}/credit/scores`, { headers: getAuthHeaders() }).catch(() => ({ data: {} })),
        axios.get(`${API}/credit/alerts`, { headers: getAuthHeaders() }).catch(() => ({ data: { alerts: [] } })),
        axios.get(`${API}/credit/tradelines`, { headers: getAuthHeaders() }).catch(() => ({ data: { tradelines: [] } })),
        axios.get(`${API}/credit/dispute/cases`, { headers: getAuthHeaders() }).catch(() => ({ data: { cases: [] } }))
      ]);
      
      setProviderStatus(statusRes.data);
      setScores(scoresRes.data.scores || null);
      setAlerts(alertsRes.data.alerts || []);
      setTradelines(tradelinesRes.data.tradelines || []);
      setDisputeCases(disputesRes.data.cases || []);
    } catch (error) {
      console.error('Failed to fetch credit data:', error);
      toast.error('Failed to load credit data');
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    try {
      setRefreshing(true);
      await axios.post(`${API}/credit/refresh`, {}, { headers: getAuthHeaders() });
      toast.success('Credit data refresh initiated');
      setTimeout(() => fetchAllData(), 2000);
    } catch (error) {
      console.error('Failed to refresh:', error);
      toast.error('Failed to refresh credit data');
    } finally {
      setRefreshing(false);
    }
  };

  const calculateBorrowingPower = async () => {
    try {
      const res = await axios.get(`${API}/credit/borrowing-power`, { headers: getAuthHeaders() });
      setBorrowingPower(res.data.borrowing_power);
      toast.success('Borrowing power calculated');
    } catch (error) {
      console.error('Failed to calculate borrowing power:', error);
      toast.error('Failed to calculate borrowing power');
    }
  };

  const getRecommendations = async () => {
    try {
      const res = await axios.get(`${API}/credit/recommendations`, { headers: getAuthHeaders() });
      setRecommendations(res.data.recommendations || []);
      toast.success('Recommendations generated');
    } catch (error) {
      console.error('Failed to get recommendations:', error);
      toast.error('Failed to generate recommendations');
    }
  };

  const getPriorityColor = (priority) => {
    const colors = {
      critical: 'text-red-500',
      high: 'text-orange-500',
      medium: 'text-yellow-500',
      low: 'text-blue-500'
    };
    return colors[priority] || 'text-gray-500';
  };

  const getSeverityColor = (severity) => {
    const colors = {
      critical: 'bg-red-500/20 text-red-400 border-red-500/30',
      high: 'bg-orange-500/20 text-orange-400 border-orange-500/30',
      medium: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
      low: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
      info: 'bg-gray-500/20 text-gray-400 border-gray-500/30'
    };
    return colors[severity] || colors.info;
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="text-center">
            <RefreshCw className="w-12 h-12 animate-spin text-[#F59E0B] mx-auto mb-4" />
            <p className="text-gray-400">Loading Credit Intelligence...</p>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-white flex items-center gap-3">
              <Shield className="text-[#F59E0B]" />
              Credit Command Center
            </h1>
            <p className="text-gray-400 mt-1">Institutional-Grade Credit Intelligence</p>
          </div>
          <button
            onClick={handleRefresh}
            disabled={refreshing}
            className="px-4 py-2 bg-[#F59E0B] hover:bg-[#F59E0B]/80 text-black rounded-lg flex items-center gap-2 disabled:opacity-50"
          >
            <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>

        {/* Provider Status */}
        <div className="bg-[#1C1C1E] border border-gray-800 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className={`w-3 h-3 rounded-full ${providerStatus?.connected ? 'bg-green-500' : 'bg-gray-500'}`} />
              <span className="text-white font-medium">
                {providerStatus?.connected ? 'Connected to Experian' : 'Not Connected'}
              </span>
              {providerStatus?.provider && (
                <span className="text-gray-400 text-sm">• Auto-monitoring active</span>
              )}
            </div>
            <div className="flex items-center gap-3">
              {providerStatus?.last_refresh && (
                <span className="text-gray-400 text-sm">
                  Last sync: {new Date(providerStatus.last_refresh).toLocaleString()}
                </span>
              )}
              {!providerStatus?.connected && (
                <button
                  onClick={() => setShowConnectProvider(true)}
                  className="px-4 py-2 bg-[#F59E0B] hover:bg-[#F59E0B]/80 text-black rounded-lg flex items-center gap-2"
                  data-testid="connect-credit-btn"
                >
                  <Shield className="w-4 h-4" />
                  Connect Credit Monitoring
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="border-b border-gray-800">
          <div className="flex gap-4">
            {['overview', 'optimization', 'disputes', 'accounts'].map(tab => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`px-4 py-2 capitalize ${
                  activeTab === tab
                    ? 'border-b-2 border-[#F59E0B] text-[#F59E0B]'
                    : 'text-gray-400 hover:text-white'
                }`}
              >
                {tab}
              </button>
            ))}
          </div>
        </div>

        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <div className="space-y-6">
            {/* Scores */}
            {scores && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {scores.fico_score && (
                  <div className="bg-[#1C1C1E] border border-gray-800 rounded-lg p-6">
                    <div className="flex items-center justify-between mb-4">
                      <span className="text-gray-400">FICO Score</span>
                      <span className="text-sm text-gray-500">{scores.bureau}</span>
                    </div>
                    <div className="text-4xl font-bold text-white">{scores.fico_score}</div>
                  </div>
                )}
                {scores.vantage_score && (
                  <div className="bg-[#1C1C1E] border border-gray-800 rounded-lg p-6">
                    <div className="flex items-center justify-between mb-4">
                      <span className="text-gray-400">VantageScore</span>
                      <span className="text-sm text-gray-500">{scores.bureau}</span>
                    </div>
                    <div className="text-4xl font-bold text-white">{scores.vantage_score}</div>
                  </div>
                )}
              </div>
            )}

            {/* Borrowing Power */}
            {!borrowingPower && (
              <div className="bg-[#1C1C1E] border border-gray-800 rounded-lg p-6">
                <button
                  onClick={calculateBorrowingPower}
                  className="w-full py-3 bg-[#F59E0B] hover:bg-[#F59E0B]/80 text-black rounded-lg flex items-center justify-center gap-2"
                >
                  <Target className="w-5 h-5" />
                  Calculate Borrowing Power
                </button>
              </div>
            )}

            {borrowingPower && (
              <div className="bg-[#1C1C1E] border border-gray-800 rounded-lg p-6">
                <h3 className="text-xl font-semibold text-white mb-4">Borrowing Power Analysis</h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                  <div>
                    <div className="text-gray-400 text-sm mb-1">Power Score</div>
                    <div className="text-2xl font-bold text-white">{borrowingPower.borrowing_power_score}</div>
                  </div>
                  <div>
                    <div className="text-gray-400 text-sm mb-1">Lending Tier</div>
                    <div className="text-sm font-medium text-[#F59E0B] uppercase">{borrowingPower.lending_tier}</div>
                  </div>
                  <div>
                    <div className="text-gray-400 text-sm mb-1">Mortgage Approval</div>
                    <div className="text-2xl font-bold text-white">{Math.round(borrowingPower.mortgage_approval_probability)}%</div>
                  </div>
                  <div>
                    <div className="text-gray-400 text-sm mb-1">Business Credit</div>
                    <div className="text-2xl font-bold text-white">{Math.round(borrowingPower.business_credit_readiness)}%</div>
                  </div>
                </div>
              </div>
            )}

            {/* Alerts */}
            {alerts.length > 0 && (
              <div className="bg-[#1C1C1E] border border-gray-800 rounded-lg p-6">
                <h3 className="text-xl font-semibold text-white mb-4 flex items-center gap-2">
                  <AlertCircle className="text-[#F59E0B]" />
                  Active Alerts ({alerts.filter(a => !a.read).length})
                </h3>
                <div className="space-y-3">
                  {alerts.slice(0, 5).map((alert, idx) => (
                    <div
                      key={idx}
                      className={`border rounded-lg p-4 ${getSeverityColor(alert.severity)}`}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="font-medium mb-1">{alert.title}</div>
                          <div className="text-sm opacity-80">{alert.message}</div>
                        </div>
                        {!alert.read && (
                          <div className="w-2 h-2 bg-current rounded-full mt-2" />
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Tradelines Summary */}
            {tradelines.length > 0 && (
              <div className="bg-[#1C1C1E] border border-gray-800 rounded-lg p-6">
                <h3 className="text-xl font-semibold text-white mb-4">Credit Accounts</h3>
                <div className="space-y-3">
                  {tradelines.map((account, idx) => (
                    <div key={idx} className="border border-gray-700 rounded-lg p-4">
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-medium text-white">{account.account_name}</span>
                        <span className="text-sm text-gray-400 capitalize">{account.account_type.replace('_', ' ')}</span>
                      </div>
                      {account.credit_limit > 0 && (
                        <div className="flex items-center justify-between text-sm">
                          <span className="text-gray-400">
                            ${account.current_balance?.toLocaleString()} / ${account.credit_limit?.toLocaleString()}
                          </span>
                          <span className={`font-medium ${
                            account.utilization_ratio < 30 ? 'text-green-500' : 
                            account.utilization_ratio < 50 ? 'text-yellow-500' : 'text-red-500'
                          }`}>
                            {account.utilization_ratio?.toFixed(1)}% util
                          </span>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Optimization Tab */}
        {activeTab === 'optimization' && (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <h2 className="text-2xl font-bold text-white">Credit Optimization</h2>
              <button
                onClick={getRecommendations}
                className="px-4 py-2 bg-[#F59E0B] hover:bg-[#F59E0B]/80 text-black rounded-lg flex items-center gap-2"
              >
                <Activity className="w-4 h-4" />
                Generate Recommendations
              </button>
            </div>

            {recommendations.length > 0 && (
              <div className="space-y-4">
                {recommendations.map((rec, idx) => (
                  <div key={idx} className="bg-[#1C1C1E] border border-gray-800 rounded-lg p-6">
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <span className={`font-bold ${getPriorityColor(rec.priority)}`}>
                            {rec.priority.toUpperCase()}
                          </span>
                          <span className="text-gray-400">•</span>
                          <span className="text-gray-400 capitalize">{rec.category}</span>
                        </div>
                        <h3 className="text-xl font-semibold text-white mb-2">{rec.title}</h3>
                        <p className="text-gray-400">{rec.description}</p>
                      </div>
                      <div className="text-right">
                        <div className="text-2xl font-bold text-[#F59E0B]">
                          +{rec.estimated_score_impact}
                        </div>
                        <div className="text-sm text-gray-400">points</div>
                      </div>
                    </div>
                    <div className="border-t border-gray-700 pt-4">
                      <div className="text-sm font-medium text-white mb-2">Action Items:</div>
                      <ul className="space-y-1">
                        {rec.action_items?.map((item, i) => (
                          <li key={i} className="text-gray-400 text-sm flex items-start gap-2">
                            <CheckCircle className="w-4 h-4 text-[#F59E0B] mt-0.5 flex-shrink-0" />
                            {item}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Disputes Tab */}
        {activeTab === 'disputes' && (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <h2 className="text-2xl font-bold text-white">Dispute Management</h2>
              <div className="text-sm text-gray-400">
                Owner approval required before submission
              </div>
            </div>

            {disputeCases.length > 0 ? (
              <div className="space-y-4">
                {disputeCases.map((dispute, idx) => (
                  <div key={idx} className="bg-[#1C1C1E] border border-gray-800 rounded-lg p-6">
                    <div className="flex items-center justify-between mb-4">
                      <div>
                        <span className="text-white font-medium capitalize">{dispute.item_type?.replace('_', ' ')}</span>
                        <div className="text-sm text-gray-400 mt-1">{dispute.status}</div>
                      </div>
                      {dispute.dispute_letter_draft && (
                        <button className="px-3 py-1 bg-gray-700 hover:bg-gray-600 text-white rounded flex items-center gap-2 text-sm">
                          <Download className="w-4 h-4" />
                          Export Letter
                        </button>
                      )}
                    </div>
                    {dispute.required_documents && (
                      <div className="border-t border-gray-700 pt-4">
                        <div className="text-sm font-medium text-white mb-2">Required Documents:</div>
                        <ul className="space-y-1">
                          {dispute.required_documents.map((doc, i) => (
                            <li key={i} className="text-gray-400 text-sm">• {doc}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className="bg-[#1C1C1E] border border-gray-800 rounded-lg p-12 text-center">
                <FileText className="w-16 h-16 text-gray-600 mx-auto mb-4" />
                <p className="text-gray-400 mb-4">No active disputes</p>
                <p className="text-sm text-gray-500">Disputes will appear here when negative items are identified</p>
              </div>
            )}
          </div>
        )}

        {/* Accounts Tab */}
        {activeTab === 'accounts' && (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <h2 className="text-2xl font-bold text-white">Credit Accounts</h2>
              <button
                onClick={() => setShowAddAccount(true)}
                className="px-4 py-2 bg-[#F59E0B] hover:bg-[#F59E0B]/80 text-black rounded-lg flex items-center gap-2"
                data-testid="add-account-btn"
              >
                <Plus className="w-4 h-4" />
                Add Account
              </button>
            </div>

            {tradelines.length > 0 ? (
              <div className="grid gap-4">
                {tradelines.map((account, idx) => (
                  <div key={idx} className="bg-[#1C1C1E] border border-gray-800 rounded-lg p-6">
                    <div className="flex items-center justify-between mb-4">
                      <div>
                        <h3 className="text-lg font-semibold text-white">{account.account_name}</h3>
                        <p className="text-sm text-gray-400 capitalize">{account.account_type?.replace('_', ' ')}</p>
                      </div>
                      <CreditCard className="w-8 h-8 text-gray-600" />
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <div>
                        <div className="text-gray-400 text-sm">Balance</div>
                        <div className="text-white font-medium">${account.current_balance?.toLocaleString()}</div>
                      </div>
                      {account.credit_limit > 0 && (
                        <>
                          <div>
                            <div className="text-gray-400 text-sm">Limit</div>
                            <div className="text-white font-medium">${account.credit_limit?.toLocaleString()}</div>
                          </div>
                          <div>
                            <div className="text-gray-400 text-sm">Utilization</div>
                            <div className={`font-medium ${
                              account.utilization_ratio < 30 ? 'text-green-500' : 
                              account.utilization_ratio < 50 ? 'text-yellow-500' : 'text-red-500'
                            }`}>
                              {account.utilization_ratio?.toFixed(1)}%
                            </div>
                          </div>
                        </>
                      )}
                      <div>
                        <div className="text-gray-400 text-sm">Status</div>
                        <div className="text-white font-medium capitalize">{account.account_status}</div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="bg-[#1C1C1E] border border-gray-800 rounded-lg p-12 text-center">
                <CreditCard className="w-16 h-16 text-gray-600 mx-auto mb-4" />
                <p className="text-gray-400 mb-4">No credit accounts added</p>
                <button
                  onClick={() => setShowAddAccount(true)}
                  className="px-4 py-2 bg-[#F59E0B] hover:bg-[#F59E0B]/80 text-black rounded-lg"
                >
                  Add Your First Account
                </button>
              </div>
            )}
          </div>
        )}

        {/* Privacy Notice */}
        <div className="bg-[#1C1C1E] border border-gray-800 rounded-lg p-4">
          <div className="flex items-start gap-3 text-sm">
            <Shield className="w-5 h-5 text-[#F59E0B] flex-shrink-0 mt-0.5" />
            <div className="text-gray-400">
              <strong className="text-white">Privacy Protected:</strong> No SSN stored. All sensitive data encrypted. 
              Owner-only access. Immutable audit trail active.
            </div>
          </div>
        </div>
      </div>

      {/* Connect Provider Modal */}
      {showConnectProvider && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <div className="bg-[#1C1C1E] border border-gray-800 rounded-lg max-w-2xl w-full">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-2xl font-bold text-white">Connect Credit Monitoring</h3>
                <button
                  onClick={() => setShowConnectProvider(false)}
                  className="text-gray-400 hover:text-white"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              <div className="space-y-6">
                <div className="bg-[#2C2C2E] border border-gray-700 rounded-lg p-6">
                  <div className="flex items-start gap-4">
                    <Shield className="w-12 h-12 text-[#F59E0B] flex-shrink-0" />
                    <div>
                      <h4 className="text-lg font-semibold text-white mb-2">Experian Credit Monitoring</h4>
                      <p className="text-gray-400 text-sm mb-4">
                        Connect your Experian account for automatic credit monitoring. Lee will check your credit daily and alert you to any changes.
                      </p>
                      <ul className="space-y-2 text-sm text-gray-400">
                        <li className="flex items-center gap-2">
                          <CheckCircle className="w-4 h-4 text-green-500" />
                          Daily automatic credit report pulls
                        </li>
                        <li className="flex items-center gap-2">
                          <CheckCircle className="w-4 h-4 text-green-500" />
                          Instant alerts on score changes, inquiries, and derogatories
                        </li>
                        <li className="flex items-center gap-2">
                          <CheckCircle className="w-4 h-4 text-green-500" />
                          Automatic dispute preparation (requires approval)
                        </li>
                        <li className="flex items-center gap-2">
                          <CheckCircle className="w-4 h-4 text-green-500" />
                          Zero manual data entry after connection
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>

                <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-4">
                  <div className="flex items-start gap-3 text-sm">
                    <AlertCircle className="w-5 h-5 text-yellow-500 flex-shrink-0 mt-0.5" />
                    <div className="text-yellow-200">
                      <strong className="block mb-1">Experian Credentials Required</strong>
                      <p className="text-yellow-200/80 mb-2">
                        To connect live credit monitoring, you need Experian Connect API credentials. 
                        Sign up at <a href="https://developer.experian.com" target="_blank" rel="noopener noreferrer" className="underline">developer.experian.com</a>
                      </p>
                      <p className="text-yellow-200/80 text-xs">
                        Once you have credentials, add them to your environment configuration.
                      </p>
                    </div>
                  </div>
                </div>

                <form onSubmit={async (e) => {
                  e.preventDefault();
                  setConnecting(true);
                  
                  try {
                    // Start OAuth flow
                    const res = await axios.post(
                      `${API}/credit/connect/start`,
                      {},
                      { headers: getAuthHeaders() }
                    );
                    
                    if (res.data.auth_url) {
                      // OAuth flow - redirect to Experian
                      toast.info('Redirecting to Experian...');
                      
                      // Store return path
                      sessionStorage.setItem('credit_oauth_return', window.location.pathname);
                      
                      // Redirect to OAuth provider
                      setTimeout(() => {
                        window.location.href = res.data.auth_url;
                      }, 500);
                    } else if (res.data.demo_flow) {
                      // Demo OAuth simulation
                      toast.info('Redirecting to Experian (Demo)...');
                      
                      // Simulate OAuth redirect
                      sessionStorage.setItem('credit_oauth_return', window.location.pathname);
                      
                      // Redirect to demo OAuth page
                      setTimeout(() => {
                        window.location.href = '/credit/oauth-demo';
                      }, 500);
                    }
                  } catch (error) {
                    console.error('Connection error:', error);
                    toast.error('Failed to start connection flow');
                    setConnecting(false);
                  }
                }} className="space-y-4">
                  <button
                    type="submit"
                    disabled={connecting}
                    className="w-full px-4 py-3 bg-[#F59E0B] hover:bg-[#F59E0B]/80 text-black font-medium rounded-lg flex items-center justify-center gap-2 disabled:opacity-50"
                  >
                    {connecting ? (
                      <>
                        <RefreshCw className="w-5 h-5 animate-spin" />
                        Redirecting to Experian...
                      </>
                    ) : (
                      <>
                        <Shield className="w-5 h-5" />
                        Connect with Experian
                      </>
                    )}
                  </button>
                </form>

                <div className="text-center text-sm text-gray-400">
                  <p>Don't have an Experian account?</p>
                  <a
                    href="https://www.experian.com/consumer-products/credit-monitoring.html"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-[#F59E0B] hover:underline"
                  >
                    Create one for free →
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Add Account Modal */}
      {showAddAccount && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <div className="bg-[#1C1C1E] border border-gray-800 rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-2xl font-bold text-white">Add Credit Account</h3>
                <button
                  onClick={() => setShowAddAccount(false)}
                  className="text-gray-400 hover:text-white"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              <form onSubmit={async (e) => {
                e.preventDefault();
                const formData = new FormData(e.target);
                const accountData = {
                  account_name: formData.get('account_name'),
                  account_type: formData.get('account_type'),
                  creditor: formData.get('creditor'),
                  current_balance: parseFloat(formData.get('current_balance') || 0),
                  credit_limit: parseFloat(formData.get('credit_limit') || 0),
                  date_opened: formData.get('date_opened'),
                  account_status: 'open',
                  payment_status: 'current'
                };

                try {
                  await axios.post(`${API}/credit/accounts/add`, accountData, { headers: getAuthHeaders() });
                  toast.success('Account added successfully');
                  setShowAddAccount(false);
                  fetchAllData();
                } catch (error) {
                  console.error('Failed to add account:', error);
                  toast.error('Failed to add account');
                }
              }} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-400 mb-2">
                    Account Name *
                  </label>
                  <input
                    type="text"
                    name="account_name"
                    required
                    placeholder="e.g., Chase Sapphire Reserve"
                    className="w-full px-4 py-2 bg-[#2C2C2E] border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-[#F59E0B]"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-400 mb-2">
                    Account Type *
                  </label>
                  <select
                    name="account_type"
                    required
                    className="w-full px-4 py-2 bg-[#2C2C2E] border border-gray-700 rounded-lg text-white focus:outline-none focus:border-[#F59E0B]"
                  >
                    <option value="credit_card">Credit Card</option>
                    <option value="mortgage">Mortgage</option>
                    <option value="auto_loan">Auto Loan</option>
                    <option value="student_loan">Student Loan</option>
                    <option value="personal_loan">Personal Loan</option>
                    <option value="line_of_credit">Line of Credit</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-400 mb-2">
                    Creditor/Lender *
                  </label>
                  <input
                    type="text"
                    name="creditor"
                    required
                    placeholder="e.g., Chase Bank"
                    className="w-full px-4 py-2 bg-[#2C2C2E] border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-[#F59E0B]"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-400 mb-2">
                      Current Balance *
                    </label>
                    <input
                      type="number"
                      name="current_balance"
                      required
                      min="0"
                      step="0.01"
                      placeholder="0.00"
                      className="w-full px-4 py-2 bg-[#2C2C2E] border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-[#F59E0B]"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-400 mb-2">
                      Credit Limit
                    </label>
                    <input
                      type="number"
                      name="credit_limit"
                      min="0"
                      step="0.01"
                      placeholder="0.00"
                      className="w-full px-4 py-2 bg-[#2C2C2E] border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-[#F59E0B]"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-400 mb-2">
                    Date Opened
                  </label>
                  <input
                    type="date"
                    name="date_opened"
                    className="w-full px-4 py-2 bg-[#2C2C2E] border border-gray-700 rounded-lg text-white focus:outline-none focus:border-[#F59E0B]"
                  />
                </div>

                <div className="bg-[#2C2C2E] border border-gray-700 rounded-lg p-4">
                  <div className="flex items-start gap-3 text-sm text-gray-400">
                    <Shield className="w-5 h-5 text-[#F59E0B] flex-shrink-0 mt-0.5" />
                    <div>
                      <strong className="text-white">Privacy Note:</strong> This data is stored securely and never shared. 
                      No full account numbers are stored.
                    </div>
                  </div>
                </div>

                <div className="flex gap-3 pt-4">
                  <button
                    type="button"
                    onClick={() => setShowAddAccount(false)}
                    className="flex-1 px-4 py-3 bg-gray-700 hover:bg-gray-600 text-white rounded-lg"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="flex-1 px-4 py-3 bg-[#F59E0B] hover:bg-[#F59E0B]/80 text-black font-medium rounded-lg"
                  >
                    Add Account
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      <Toaster position="top-right" />
    </Layout>
  );
};

export default CreditCommandCenter;
